import { Authenticator, Button, useAuthenticator } from '@aws-amplify/ui-react';
import { Auth } from 'aws-amplify';
import { Box } from '@mui/material';
import React from 'react';
import { styled } from '@mui/material/styles';
import { configureAmplify } from 'shared/components/auth/utils';
import { pools } from 'shared/components/auth/config';
import { Site } from 'shared/hooks/use-site-detection.types';
import { useSignInEmailFromDom } from 'shared/components/auth/my-authenticator/use-sign-in-email-from-dom';
import { useSignInEmailValidation } from 'shared/components/auth/my-authenticator/use-sign-in-email-validation';
import { OgpSsoSection } from 'shared/components/auth/my-authenticator/ogp-sso-section';
import { shouldShowOgpSsoSection } from 'shared/components/auth/my-authenticator/ogp-sso-visibility';
import { useGetLoginConfig } from 'shared/hooks/query-hooks/use-get-login-config';
import { useSiteDetection } from 'shared/hooks/use-site-detection';
import { SignInFooter } from './ogp-sign-in-footer';
import { useCheckCompanyHasSsoEnforced } from './use-check-company-has-sso-enforced';
import { LOGO_PATH } from '../../../../config';

export const MyAuthenticator = () => {
  const { site } = useSiteDetection();
  const { checkSsoEnforcement } = useCheckCompanyHasSsoEnforced();

  const loginConfigQuery = useGetLoginConfig();
  const allowOGPSSO =
    loginConfigQuery.status === 'success' ? loginConfigQuery.data.allowOGPSSO : false;
  const formFields: Exclude<React.ComponentProps<typeof Authenticator>['formFields'], undefined> = {
    signIn: {
      username: {
        labelHidden: false,
        placeholder: '',
        isRequired: true,
        type: 'email',
      },
      password: {
        labelHidden: false,
        isRequired: true,
        placeholder: '',
      },
    },
    resetPassword: {
      username: {
        labelHidden: false,
        isRequired: true,
        placeholder: '',
      },
    },
    confirmResetPassword: {
      confirmation_code: {
        labelHidden: false,
        isRequired: true,
        placeholder: '',
      },
      password: {
        labelHidden: false,
        isRequired: true,
        placeholder: '',
      },
      confirm_password: {
        labelHidden: false,
        isRequired: true,
        placeholder: '',
      },
    },
  };

  return (
    <StyledAuthenticator
      services={{
        handleSignIn: async (args) => {
          configureAmplify(pools[site]);
          await checkSsoEnforcement(args);
          return await Auth.signIn(args);
        },
      }}
      components={{
        Header: AuthenticatorHeader,
        ResetPassword: {
          Header: ResetPasswordHeader,
        },
        SignIn: {
          Footer: () => <SignInFooterContent site={site} allowOGPSSO={allowOGPSSO} />,
        },
      }}
      formFields={formFields}
      hideSignUp
    />
  );
};

function AuthenticatorHeader() {
  return <Logo src={LOGO_PATH} alt="Logo" />;
}

function ResetPasswordHeader() {
  return <strong style={{ display: 'block' }}>Wachtwoord opnieuw instellen</strong>;
}

function SignInFooterContent({ site, allowOGPSSO }: { site: Site; allowOGPSSO: boolean }) {
  useSignInEmailValidation();
  const showOgpSso = shouldShowOgpSsoSection(site, allowOGPSSO);

  const { toResetPassword } = useAuthenticator();
  const signInEmail = useSignInEmailFromDom(showOgpSso);

  return (
    <Box display={'flex'} flexDirection={'column'} alignItems={'center'} width={'100%'}>
      <OgpSsoSection visible={showOgpSso} />
      <StyledForgotPasswordLink onClick={toResetPassword} variation="link">
        Wachtwoord vergeten?
      </StyledForgotPasswordLink>
      {site !== 'hq' && <SignInFooter />}
    </Box>
  );
}

const Logo = styled('img')(() => ({
  width: '100%',
  marginBottom: '75px',
  display: 'block',
}));

const StyledAuthenticator = styled(Authenticator)(() => ({
  '.amplify-alert--error': {
    textWrap: 'nowrap',
  },
}));

const StyledForgotPasswordLink = styled(Button)(({ theme }) => ({
  fontWeight: 'normal',
  height: 'fit-content',
  marginTop: theme.spacing(1),
  alignSelf: 'center',
}));
